import { useCallback, useMemo, useState } from 'react';

import { CircularProgress, Typography } from '@mui/material';
import MUIDataTable, {
    type MUIDataTableColumnDef,
    type MUIDataTableOptions,
    type MUIDataTableState
} from 'mui-datatables';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

declare module '@mui/material/styles' {
    interface Components {
        [key: string]: any
    }
}

const StyledCircularProgress = styled(CircularProgress)`
  margin-left: 15px;
  position: relative;
  top: 4px;
`;

const defaultDatatablesOptions: MUIDataTableOptions = {
    filter: true,
    filterType: 'dropdown',
    responsive: 'simple',
    serverSide: true,
    rowsPerPageOptions: [],
    elevation: 0
};

export type DataTableProvider = (tableState: MUIDataTableState) => Promise<{ data: any[], count: number }>;

export type APIDataTableProps = {
    title?: string,
    columns: MUIDataTableColumnDef[],
    extraOptions: MUIDataTableOptions,
    dataProvider: DataTableProvider
};

/**
 * Easily create a data table with remote data provider.
 *
 * @param props
 * @constructor
 */
export const APIDataTable: React.FC<APIDataTableProps> = (props) => {
    const { t } = useTranslation('common');
    const { title, columns, extraOptions, dataProvider } = props;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [externalTableState, setExternalTableState] = useState<any>({});

    const handleTableChange = useCallback(async (action, datatableState) => {
        if (['tableInitialized', 'changePage', 'sort', 'filter'].includes(action)) {
            setIsLoading(true);
            setExternalTableState(await dataProvider(datatableState));
            setIsLoading(false);
        }
    }, [dataProvider]);

    const options: MUIDataTableOptions = useMemo(() => {
        // Define new object for override options to help readability.
        const overrideOptions = {
            count: externalTableState.count,
            onTableChange: handleTableChange,
            onTableInit: handleTableChange,
            textLabels: {
                body: {
                    noMatch: isLoading ? t('loading') : t('table.noMatching')
                }
            }
        } as MUIDataTableOptions;

        return { ...defaultDatatablesOptions, ...overrideOptions, ...(extraOptions || {}) };
    }, [externalTableState.count, extraOptions, handleTableChange, isLoading, t]);

    const fancyTitle = useMemo(() => {
        if (!title) return '';

        return (
            <Typography variant="h6">
                {title}
                {isLoading && <StyledCircularProgress size={24}/>}
            </Typography>
        );
    }, [isLoading, title]);

    return (
        <MUIDataTable
            title={fancyTitle}
            data={externalTableState.data}
            options={options}
            columns={columns}
        />
    );
};
